import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Camera } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import OtpVerifyPanel from "./OtpVerifyPanel";
import { getMyProfile, updateProfile, addBusinessInfo, uploadSingleImage, resendProfileOtp } from "../../services/accountService";
import { color, radius } from "../../styles/theme";

const normalizePhone = (phone) => `${phone ?? ""}`.replace(/\s+/g, "");

const FULL_NAME_MAX = 50;

const maxDob = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 12);
  return d.toISOString().split("T")[0];
};

/**
 * Personal profile — ported from mobile's profile-update screen
 * (src/Screens/profile-update). Phone editing is inline here (not a separate
 * screen — see the approved plan) and preserves mobile's exact
 * verify-after-save behavior: after UpdateProfile succeeds, if the phone
 * changed (or was never confirmed) and the email is confirmed, an inline OTP
 * panel appears instead of a second navigation.
 */
const ProfileTab = () => {
  const { t } = useTranslation(["settings", "common"]);
  const { user, updateUser } = useAuth();

  const [nickName, setNickName] = useState(user?.NickName || "");
  const [fullName, setFullName] = useState(user?.Name || "");
  const [phone, setPhone] = useState(user?.PhoneNumber || "");
  const [gender, setGender] = useState(user?.Gender ?? "");
  const [dob, setDob] = useState(user?.DateOfBirth ? user.DateOfBirth.slice(0, 10) : "");
  const [city, setCity] = useState(user?.City || "");
  const [country, setCountry] = useState(user?.Country || "");
  const [avatar, setAvatar] = useState(user?.DisplayPicture || "");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [changingEmail, setChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.Email || "");
  const [savingEmail, setSavingEmail] = useState(false);

  const [otpPending, setOtpPending] = useState(null); // { phone } | null

  // Apple private-relay accounts are the one case mobile lets you edit email inline.
  const emailEditableInline = (user?.Email || "").toLowerCase().includes("privaterelay.appleid.com");

  const refreshProfile = async () => {
    const res = await getMyProfile();
    const fresh = res.data?.Result || res.data;
    if (fresh) updateUser(fresh);
    return fresh;
  };

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const url = await uploadSingleImage(file);
      if (url) setAvatar(url);
      else toast.error(t("profileTab.toasts.avatarUploadFailed"));
    } catch {
      toast.error(t("profileTab.toasts.avatarUploadFailed"));
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!nickName.trim()) {
      toast.error(t("profileTab.toasts.nicknameRequired"));
      return;
    }
    const trimmedFullName = fullName.trim();
    if (!trimmedFullName || trimmedFullName.length < 2) {
      toast.error(t("profileTab.toasts.fullNameRequired"));
      return;
    }
    if (trimmedFullName.length > FULL_NAME_MAX) {
      toast.error(t("profileTab.toasts.fullNameTooLong", { max: FULL_NAME_MAX }));
      return;
    }
    if (dob && dob > maxDob()) {
      toast.error(t("profileTab.toasts.dobTooYoung"));
      return;
    }
    setSaving(true);
    try {
      const cleanedNewPhone = normalizePhone(phone);
      const cleanedOldPhone = normalizePhone(user?.PhoneNumber);

      const payload = {
        name: trimmedFullName,
        NickName: nickName,
        country,
        city,
        dateOfBirth: dob || null,
        Latitude: user?.Latitude,
        Longitude: user?.Longitude,
        DisplayPicture: avatar,
        PhoneNumber: cleanedNewPhone,
        Gender: gender || null,
        UserName: user?.UserName,
        Address: user?.Address || "",
        State: user?.State || "",
        PostalCode: user?.PostalCode || "",
      };

      // Mobile omits PhoneNumber entirely from the payload when it's unchanged
      // and already confirmed — matched here.
      if (cleanedNewPhone === cleanedOldPhone && user?.PhoneNumberConfirmed !== false) {
        delete payload.PhoneNumber;
      }

      const res = await updateProfile(payload);

      const hasPhoneChanged = cleanedNewPhone !== cleanedOldPhone;
      const shouldVerifyPhone = hasPhoneChanged || user?.PhoneNumberConfirmed === false;

      if (shouldVerifyPhone && user?.EmailConfirmed && cleanedNewPhone) {
        if (!hasPhoneChanged) {
          // Backend only auto-sends a code when PhoneNumber changes in the
          // payload — since it didn't change here, request one explicitly.
          await resendProfileOtp(user?.Email, cleanedNewPhone);
        }
        setOtpPending({ phone: cleanedNewPhone });
        toast.success(res.data?.Message || t("profileTab.toasts.updatedVerifyPhone"));
        return;
      }

      toast.success(res.data?.Message || t("profileTab.toasts.updatedSuccess"));
      await refreshProfile();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("profileTab.toasts.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!newEmail.trim() || newEmail === user?.Email) {
      toast.info(t("profileTab.toasts.enterDifferentEmail"));
      return;
    }
    setSavingEmail(true);
    try {
      const isPromoter = user?.UserType === "Promoter";
      if (isPromoter) {
        await addBusinessInfo({ Email: newEmail });
      } else {
        await updateProfile({ Email: newEmail });
      }
      toast.success(t("profileTab.toasts.emailConfirmationSent"));
      setChangingEmail(false);
      await refreshProfile();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("profileTab.toasts.emailUpdateFailed"));
    } finally {
      setSavingEmail(false);
    }
  };

  if (otpPending) {
    return (
      <OtpVerifyPanel
        userId={user?.UserId}
        email={user?.Email}
        phoneNumber={otpPending.phone}
        onVerified={async () => {
          setOtpPending(null);
          await refreshProfile();
        }}
        onCancel={() => setOtpPending(null)}
      />
    );
  }

  return (
    <div style={s.wrap}>
      <div style={s.avatarRow}>
        <div style={s.avatarCircle}>
          {avatar ? <img src={avatar} alt="" style={s.avatarImg} /> : <span style={s.avatarInitial}>{(nickName || "P").charAt(0).toUpperCase()}</span>}
        </div>
        <label className="btn btn-secondary btn-sm" style={s.avatarBtn}>
          <Camera size={13} strokeWidth={2.25} /> {avatarUploading ? t("profileTab.uploading") : t("profileTab.changePhoto")}
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarPick} disabled={avatarUploading} />
        </label>
      </div>

      <Input
        label={t("profileTab.nicknameLabel")}
        value={nickName}
        onChange={(e) => setNickName(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
        placeholder={t("profileTab.nicknamePlaceholder")}
      />
      <Input
        label={t("profileTab.fullNameLabel")}
        value={fullName}
        onChange={(e) => setFullName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, "").slice(0, FULL_NAME_MAX))}
        placeholder={t("profileTab.fullNamePlaceholder")}
        maxLength={FULL_NAME_MAX}
      />

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("profileTab.emailLabel")}</label>
        {emailEditableInline ? (
          <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
        ) : changingEmail ? (
          <div>
            <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={t("profileTab.newEmailPlaceholder")} />
            <div style={s.inlineActions}>
              <Button onClick={handleSaveEmail} disabled={savingEmail}>{savingEmail ? t("profileTab.savingEmail") : t("profileTab.saveEmail")}</Button>
              <Button variant="secondary" onClick={() => { setChangingEmail(false); setNewEmail(user?.Email || ""); }} disabled={savingEmail}>
                {t("common:actions.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div style={s.readonlyRow}>
            <span style={s.readonlyValue}>{user?.Email}</span>
            <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => setChangingEmail(true)}>{t("profileTab.changeEmail")}</button>
          </div>
        )}
      </div>

      <Input label={t("profileTab.phoneNumberLabel")} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 151 12345678" />

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("profileTab.genderLabel")}</label>
        <select className="input" value={gender} onChange={(e) => setGender(e.target.value)}>
          <option value="">{t("profileTab.selectOption")}</option>
          <option value="Male">{t("profileTab.male")}</option>
          <option value="Female">{t("profileTab.female")}</option>
          <option value="Other">{t("profileTab.other")}</option>
        </select>
      </div>

      <Input label={t("profileTab.dobLabel")} type="date" value={dob} max={maxDob()} onChange={(e) => setDob(e.target.value)} style={{ colorScheme: "light" }} />
      <p style={{ ...s.hint, marginTop: -12 }}>{t("profileTab.dobHint", { date: maxDob() })}</p>

      <Input label={t("profileTab.cityLabel")} value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("profileTab.cityPlaceholder")} />
      <Input label={t("profileTab.countryLabel")} value={country} onChange={(e) => setCountry(e.target.value)} placeholder={t("profileTab.countryPlaceholder")} />

      <Button onClick={handleSave} disabled={saving} style={{ marginTop: 8 }}>
        {saving ? t("profileTab.saving") : t("profileTab.saveButton")}
      </Button>
    </div>
  );
};

const s = {
  wrap: { maxWidth: 480 },
  avatarRow: { display: "flex", alignItems: "center", gap: 16, marginBottom: 26 },
  avatarCircle: {
    width: 72, height: 72, borderRadius: "50%", background: color.primary500,
    display: "flex", alignItems: "center", justifyContent: "center",
    overflow: "hidden", flexShrink: 0,
  },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  avatarInitial: { color: "#fff", fontSize: 26, fontWeight: 700 },
  avatarBtn: { display: "inline-flex" },
  fieldLabel: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  readonlyRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 13px", background: color.surface, border: `1.5px solid ${color.border}`,
    borderRadius: radius.md,
  },
  readonlyValue: { fontSize: 13.5, color: color.gray900 },
  linkBtn: { color: color.primary600 },
  inlineActions: { display: "flex", gap: 10, marginTop: 10 },
  hint: { fontSize: 11, color: color.textMuted, marginBottom: 16 },
};

export default ProfileTab;
